import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Send, FileText, Scale, ShoppingCart } from 'lucide-react';
import logger from '../../utils/logger';
import brand from '../../config/brand';

const InformationsLegales = () => {
  const [activeTab, setActiveTab] = useState('mentions');
  const [showContactModal, setShowContactModal] = useState(false);
  const [legalContent, setLegalContent] = useState({ mentions_legales: '', cgv: '', cgu: '' });
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [contactForm, setContactForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: ''
  });

  useEffect(() => {
    const loadLegalContent = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/site-settings/legal`);
        if (response.ok) {
          const data = await response.json();
          setLegalContent(data);
        }
      } catch (error) {
        // Utiliser le contenu par défaut
      }
    };
    loadLegalContent();
  }, [backendUrl]);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    logger.debug('Formulaire contact depuis informations légales:', contactForm);
    alert('Votre message a été envoyé avec succès !');
    setShowContactModal(false);
    setContactForm({ nom: '', prenom: '', email: '', telephone: '', message: '' });
  };

  const tabs = [
    { id: 'mentions', label: 'Mentions Légales', icon: Scale },
    { id: 'cgu', label: 'CGU', icon: FileText },
    { id: 'cgv', label: 'CGV', icon: ShoppingCart },
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Bouton Contact flottant */}
          <div className="fixed bottom-6 right-6 z-40">
            <button
              onClick={() => setShowContactModal(true)}
              className="bg-brand-primary text-white p-4 rounded-full shadow-lg hover:bg-brand-primary-hover transition-all duration-300 hover:scale-110"
              title="Nous contacter"
              data-testid="contact-button"
            >
              <MessageSquare size={24} />
            </button>
          </div>

          {/* Onglets */}
          <div className="bg-white rounded-t-lg shadow-lg">
            <div className="flex border-b">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'text-brand-primary border-b-2 border-brand-primary bg-blue-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <Icon size={18} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.id.toUpperCase()}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contenu */}
          <div className="bg-white rounded-b-lg shadow-lg p-8">
            
            {/* MENTIONS LÉGALES */}
            {activeTab === 'mentions' && (
              <div data-testid="content-mentions">
                <h1 className="text-3xl font-bold text-brand-text mb-8">Mentions Légales</h1>
                
                {legalContent.mentions_legales ? (
                  <div className="prose max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {legalContent.mentions_legales}
                  </div>
                ) : (
                  <div className="space-y-8 text-gray-700 leading-relaxed">
                    <section>
                      <h2 className="text-xl font-semibold text-brand-primary mb-4">Identification de l'entreprise</h2>
                      <div className="bg-gray-50 p-6 rounded-lg">
                        <p className="mb-2"><strong>Raison sociale :</strong> {brand.legalName}</p>
                        <p className="mb-2"><strong>Forme juridique :</strong> {brand.legalForm}</p>
                        <p className="mb-2"><strong>Capital social :</strong> {brand.capital}</p>
                        <p className="mb-2"><strong>RCS :</strong> {brand.rcs}</p>
                        <p className="mb-2"><strong>Représentée par :</strong> {brand.president}, président</p>
                      </div>
                    </section>

                    <section>
                      <h2 className="text-xl font-semibold text-brand-primary mb-4">Responsable de publication</h2>
                      <p>M. Sokha Tan</p>
                      <p><strong>Contact :</strong> gestion@asarcsg.com</p>
                    </section>

                    <section>
                      <h2 className="text-xl font-semibold text-brand-primary mb-4">Activité</h2>
                      <p>
                        Courtier en assurances, toutes assurances IARD, santé et prévoyance 
                        pour particuliers et professionnels.
                      </p>
                    </section>
                  </div>
                )}
              </div>
            )}

            {/* CGU */}
            {activeTab === 'cgu' && (
              <div data-testid="content-cgu">
                <div className="flex items-center mb-8">
                  <FileText className="text-brand-primary mr-4" size={36} />
                  <h1 className="text-3xl font-bold text-brand-text">Conditions Générales d'Utilisation</h1>
                </div>
                
                {legalContent.cgu ? (
                  <div className="prose max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {legalContent.cgu}
                  </div>
                ) : (
                  <div className="space-y-8 text-gray-700 leading-relaxed">
                    <section>
                      <h2 className="text-xl font-semibold text-brand-primary mb-4">Préambule</h2>
                      <p className="mb-4">
                        Le site {brand.websiteUrl}, exploité par {brand.legalName}, 
                        propose des services de courtage en assurance.
                      </p>
                      <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-brand-primary">
                        <p className="font-semibold">
                          En accédant au site ou en utilisant les services proposés, l'utilisateur accepte sans réserve les présentes CGU.
                        </p>
                      </div>
                    </section>
                  </div>
                )}
              </div>
            )}

            {/* CGV */}
            {activeTab === 'cgv' && (
              <div data-testid="content-cgv">
                <div className="flex items-center mb-8">
                  <ShoppingCart className="text-brand-primary mr-4" size={36} />
                  <h1 className="text-3xl font-bold text-brand-text">Conditions Générales de Vente</h1>
                </div>
                
                {legalContent.cgv ? (
                  <div className="prose max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {legalContent.cgv}
                  </div>
                ) : (
                  <div className="space-y-8 text-gray-700 leading-relaxed">
                    <section>
                      <h2 className="text-xl font-semibold text-brand-primary mb-4">Préambule</h2>
                      <p className="mb-4">
                        {brand.legalName} est une société exerçant une activité de courtage en assurance.
                      </p>
                      <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-brand-primary">
                        <p className="font-semibold">
                          {brand.name} se réserve le droit de modifier ces CGV à tout moment.
                        </p>
                      </div>
                    </section>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Modal Contact */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-brand-text">Nous contacter</h2>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="close-contact-modal"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.nom}
                      onChange={(e) => setContactForm({...contactForm, nom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.prenom}
                      onChange={(e) => setContactForm({...contactForm, prenom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                  <input
                    type="tel"
                    required
                    value={contactForm.telephone}
                    onChange={(e) => setContactForm({...contactForm, telephone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    rows="4"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
                    placeholder="Votre message..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-brand-primary text-white py-3 rounded-lg font-semibold hover:bg-brand-primary-hover transition-colors flex items-center justify-center"
                  data-testid="submit-contact"
                >
                  <Send className="mr-2" size={18} />
                  Envoyer le message
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InformationsLegales;
